import React, { useState } from 'react'
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  message,
  Tag,
  Select,
  Card,
  Divider
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { attributes } from '../helper/data.character'
import { createCharacter } from '../../../../../services/api/aiVideoGeneration'

const FormItem = Form.Item
const Option = Select.Option

const AddCharacterModal = ({ form, onCreate, onClose: onCancel }) => {
  const [image, setImage] = useState(null)
  const [selectedAttributes, setSelectedAttributes] = useState([])
  const [isCreatingCharacter, setIsCreatingCharacter] = useState(false)

  const handleImageUpload = file => {
    const reader = new FileReader()
    reader.onload = function(e) {
      setImage(e.target.result)
    }
    reader.readAsDataURL(file)
    return false
  }

  // Helper function to extract apparel items from selected attributes
  const extractApparelItems = () => {
    const apparelAttributes = selectedAttributes.filter(
      attr =>
        attr.attribute.toLowerCase().includes('apparel') ||
        attr.attribute.toLowerCase().includes('clothing') ||
        attr.attribute.toLowerCase().includes('outfit')
    )
    return apparelAttributes.map(attr => attr.option)
  }

  // Helper function to get gender from selected attributes
  const getGender = () => {
    const genderAttr = selectedAttributes.find(
      attr =>
        attr.attribute.toLowerCase().includes('gender') ||
        attr.attribute.toLowerCase() === 'sex'
    )
    return genderAttr ? genderAttr.option.toLowerCase() : 'male' // default to male
  }

  const handleSubmit = async () => {
    try {
      const fieldsError = await new Promise(resolve => {
        form.validateFields((err, values) => {
          resolve({ err, values })
        })
      })

      if (fieldsError.err) {
        message.error('Please fill in all required fields!')
        return
      }

      const { values } = fieldsError

      // Removed the mandatory attribute check - attributes are now optional

      if (!image) {
        message.error('Please upload an image!')
        return
      }

      setIsCreatingCharacter(true)

      // Prepare the payload according to your API structure
      const payload = {
        gender: getGender(),
        apparel_items: extractApparelItems(),
        original_prompt: values.imagePrompt || null, // Optional field
        character_tag: null, // You can modify this based on your needs
        character_name: values.name,
        reference_image_uri: image, // Always required
        platform: 'openai' // You can make this configurable if needed
      }

      console.log('payload', payload)

      // Call the API
      const response = await createCharacter(payload)

      // Handle successful response
      message.success('Character created successfully!')

      // Call the parent's onCreate callback with the response data
      if (onCreate) {
        onCreate({
          ...values,
          image,
          attributes: selectedAttributes,
          imageSource: image,
          imagePrompt: values.imagePrompt,
          apiResponse: response && response.data
        })
      }

      // Reset form state
      form.resetFields()
      setImage(null)
      setSelectedAttributes([])

      // Close the modal
      onCancel()
    } catch (error) {
      console.error('Error creating character:', error)
      message.error(
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
          'Failed to create character. Please try again.'
      )
    } finally {
      setIsCreatingCharacter(false)
    }
  }

  const handleAttributeChange = (attributeName, option) => {
    // Find the attribute definition
    const attributeObj = attributes.find(attr => attr.name === attributeName)

    if (!attributeObj) {
      return
    }

    // Remove existing selection for this attribute if it exists
    const filteredAttributes = selectedAttributes.filter(
      attr => attr.attribute !== attributeName
    )

    // If option is null/undefined (cleared), just remove the attribute
    if (!option) {
      setSelectedAttributes(filteredAttributes)
      return
    }

    // Add the new selection
    setSelectedAttributes([
      ...filteredAttributes,
      {
        attribute: attributeName,
        option: option,
        label: attributeObj.label
      }
    ])
  }

  return (
    <Modal
      visible={true}
      title="Create New Character"
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Create"
      cancelText="Cancel"
      width={500}
      centered
      bodyStyle={{ padding: '20px' }}
      confirmLoading={isCreatingCharacter}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Character Name */}
        <FormItem label="Character Name" hasFeedback>
          {form.getFieldDecorator('name', {
            rules: [
              { required: true, message: 'Please enter the character name!' }
            ]
          })(<Input placeholder="Enter character name" />)}
        </FormItem>

        {/* Character Image */}
        <FormItem label="Character Image" required>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Upload beforeUpload={handleImageUpload} showUploadList={false}>
              <Button>
                <UploadOutlined /> Upload Image
              </Button>
            </Upload>
            <div style={{ color: '#888', fontSize: '12px' }}>
              Recommended: 300x400px
            </div>
          </div>
        </FormItem>

        {/* Optional Image Prompt */}
        <FormItem label="Image Prompt (Optional)">
          {form.getFieldDecorator('imagePrompt')(
            <Input.TextArea
              placeholder="Optional: Describe the character or add additional context..."
              rows={3}
            />
          )}
        </FormItem>

        {/* Image Preview */}
        {image && (
          <div style={{ marginTop: '10px', textAlign: 'center' }}>
            <img
              src={image}
              alt="Character"
              style={{
                maxWidth: '150px',
                maxHeight: '200px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>
        )}

        {/* Attributes Section */}
        <Card>
          <div style={{ marginBottom: '16px' }}>
            <h3>Character Attributes</h3>
            <div style={{ color: 'gray', fontSize: '12px' }}>
              ( Optional - select attributes to customize your character )
            </div>
          </div>
          <Divider />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px'
            }}
          >
            {attributes.map(attr => (
              <FormItem
                key={attr.name}
                label={attr.label}
                style={{ marginBottom: '12px' }}
              >
                <Select
                  placeholder={`Select ${attr.label.toLowerCase()}`}
                  onChange={value => handleAttributeChange(attr.name, value)}
                  value={
                    selectedAttributes.find(a => a.attribute === attr.name)
                      ? selectedAttributes.find(a => a.attribute === attr.name)
                          .option
                      : undefined
                  }
                  style={{ width: '100%' }}
                  allowClear
                >
                  {attr.options.map(option => (
                    <Option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            ))}
          </div>
        </Card>

        {/* Display Selected Attributes */}
        <div>
          <h4>Selected Attributes</h4>
          <div
            style={{
              padding: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#f4f5fa'
            }}
          >
            {selectedAttributes.length === 0 ? (
              <div style={{ color: '#bbb', textAlign: 'center' }}>
                No attributes selected
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedAttributes.map(attr => (
                  <Tag
                    key={attr.attribute}
                    style={{
                      backgroundColor: '#e6f7ff',
                      borderColor: '#91d5ff',
                      color: '#0050b3',
                      borderRadius: '20px'
                    }}
                  >
                    <b>{attr.label}:</b>{' '}
                    {attr.option &&
                      attr.option.charAt(0).toUpperCase() +
                        attr.option.slice(1)}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default Form.create()(AddCharacterModal)
